import { Injectable } from "@angular/core";
import { SelfSchedulingProxy } from "./self.scheduling.proxy";
import * as selfScheduling from "../interfaces/Iself.scheduling";
import {
	ICheckCanNewOrderAndScheduleRequest,
	ILocationSelectionRequest,
	ILocationSelectionResponse,
	IOrgInstructionRequest,
	IPortalScheduleChangeMessage,
	ISearchStudyRequest,
	ISPSInstructionRequest,
	RulesValidationOverride
} from "../interfaces/Iself.scheduling";
import { HttpObservable } from "../../../common/http-helpers/http.wrapper";
import * as moment from "moment";

/**
 ****************************************************************
 * Service class for self scheduling.
 *
 * Created by Shai Zelman on 06/02/2020.
 ****************************************************************
 */
@Injectable({
	providedIn: "root"
})
export class SelfSchedulingService {
	constructor(private selfSchedulingProxy: SelfSchedulingProxy) { }

	/**
	 * Gets the list of orders and appointments for the patient.
	 * @returns An HttpObservable with the list of appointments.
	 */
	public getOrdersAndAppointments(): HttpObservable<number[]> {
		return this.selfSchedulingProxy.getOrdersAndAppointments();
	}

	public getAllSuggestionsForDay(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		searchesPerDay: selfScheduling.ISearchesPerDay,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {
		let normalStartTime: Date = new Date(startDate);
		normalStartTime.setMilliseconds(0);

		let normalEndTime: Date = new Date(endDate);
		normalEndTime.setMilliseconds(0);
		return this.selfSchedulingProxy.getAllSuggestionsForDay(sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys,
			this.normalizeDaysTimeOptions(daysTimeOptions), searchesPerDay, opCodeKey, selectedResources);
	}

	public getPortalPatientForLevel(patientDatalevel: selfScheduling.PatientDataLevel, key?: number): HttpObservable<{ patient: any }> {
		switch (patientDatalevel) {
			case selfScheduling.PatientDataLevel.Patient:
				return this.getPortalPatient();
			case selfScheduling.PatientDataLevel.Order:
				return this.getPortalOrder(key);
		}
	}

	public getPortalPatient(): HttpObservable<{ patient: any }> {
		return this.selfSchedulingProxy.getPortalPatient();
	}

	public getPortalOrder(orderKey: number): HttpObservable<{ patient: any }> {
		return this.selfSchedulingProxy.getPortalOrder(orderKey);
	}

	public findAvailableDaysInRange(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		searchesPerDay: selfScheduling.ISearchesPerDay,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {
		let normalStartTime: Date = new Date(startDate);
		normalStartTime.setMilliseconds(0);

		let normalEndTime: Date = new Date(endDate);
		normalEndTime.setMilliseconds(0);
		return this.selfSchedulingProxy.findAvailableDaysInRange(sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys,
			this.normalizeDaysTimeOptions(daysTimeOptions), searchesPerDay, opCodeKey, selectedResources);
	}

	/**
	 * Gets available time slots for the selected studies using the recommended results algorithm.
	 * @param sessionId
	 * @param studies
	 * @param orderKey
	 * @param requestedProcedureId
	 * @param startDate
	 * @param endDate
	 * @param orgStructureKeys
	 * @param daysTimeOptions
	 * @param opCodeKey
	 * @param selectedResources
	 * @returns An HttpObservable containing the data.
	 */
	public getRecommendedTimeSlots(
		sessionId: string,
		studies: selfScheduling.ISelectedStudy[],
		orderKey: number,
		requestedProcedureId: number,
		startDate: Date,
		endDate: Date,
		orgStructureKeys: number[],
		daysTimeOptions: selfScheduling.IDaysTimeOptions,
		opCodeKey: number,
		selectedResources: { [resourceRole: number]: number[] } = null
	): HttpObservable<selfScheduling.ISearchResult[][]> {

		let normalStartTime: Date = new Date(startDate);
		normalStartTime.setMilliseconds(0);

		let normalEndTime: Date = new Date(endDate);
		normalEndTime.setMilliseconds(0);

		return this.selfSchedulingProxy.getRecommendedTimeSlots(sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys,
			this.normalizeDaysTimeOptions(daysTimeOptions), opCodeKey, selectedResources);
	}

	/**
	 * Gets a list of Modality Type Groups.
	 * @returns An HttpObservable containing an array of Modality Type Groups.
	 */
	public getModalityTypeGroups(): HttpObservable<any[]> {
		return this.selfSchedulingProxy.getModalityTypeGroups();
	}

	/**
	 * Gets a list of Holidays for a given month.
	 * @returns An HttpObservable containing an array of Modality Type Groups.
	 */
	public getHolidaysForMonth(month: Date): HttpObservable<any[]> {
		return this.selfSchedulingProxy.getHolidaysForMonth(month);
	}

	public getSpsCodesBySearchTerm(request: ISearchStudyRequest): HttpObservable<any[]> {
		return this.selfSchedulingProxy.getSpsCodesBySearchTerm(request);
	}

	public getCommonExams(languageKey: number): HttpObservable<any[]> {
		return this.selfSchedulingProxy.getCommonExams(languageKey);
	}

	public getAllExams(languageKey: number): HttpObservable<any[]> {
		return this.selfSchedulingProxy.getAllExams(languageKey);
	}

	public checkCanNewOrderAndSchedule(request: ICheckCanNewOrderAndScheduleRequest): HttpObservable<boolean> {
		return this.selfSchedulingProxy.checkCanNewOrderAndSchedule(request);
	}

	public getLocationSelectionData(request: ILocationSelectionRequest): HttpObservable<ILocationSelectionResponse> {
		return this.selfSchedulingProxy.getLocationSelectionData(request);
	}

	public getCancellationReasons(langKey: number): HttpObservable<selfScheduling.ICancellationReason[]> {
		return this.selfSchedulingProxy.getCancellationReasons(langKey);
	}

	public cancelStudy(request: selfScheduling.IChangeStatusRequest): HttpObservable<any> {
		return this.selfSchedulingProxy.cancelStudy(request);
	}

	public scheduleStudies(scheduleData: selfScheduling.IScheduleData, selectedStudies: selfScheduling.ISelectedStudy[],
		opCodeKey: number, orderKey: number = -1, override: RulesValidationOverride = RulesValidationOverride.Default): HttpObservable<{ [spsId: number]: selfScheduling.IScheduleResponseDetails }> {

		scheduleData.selectedScheduling = scheduleData.selectedScheduling.map((scheduleEntry: selfScheduling.ISearchResult) => {
			const currentSelectedStudy: selfScheduling.ISelectedStudy = selectedStudies.find((selectedStudy) => selectedStudy.spsCodeKey === scheduleEntry.SPS_Code_Key);

			return {
				...scheduleEntry,
				rpCodeKey: currentSelectedStudy.rpCodeKey,
				procedureCodeKeys: currentSelectedStudy.procedureKeys,
				selectedResourceAllocation: null
			};
		});

		return this.selfSchedulingProxy.scheduleStudies(scheduleData, override, opCodeKey, orderKey);
	}

	public rescheduleStudies(
		scheduleData: selfScheduling.ISearchResult[], 
		selectedStudies: selfScheduling.ISelectedStudy[],
		opCodeKey: number, 
		orderKey: number = -1, 
		override: RulesValidationOverride = RulesValidationOverride.Default
	): HttpObservable<{ [spsId: number]: selfScheduling.IScheduleResponseDetails }> {
		return this.selfSchedulingProxy.rescheduleStudies(scheduleData.map((scheduleEntry: selfScheduling.ISearchResult) => {
			const currentSelectedStudy: selfScheduling.ISelectedStudy = selectedStudies.find((selectedStudy) => selectedStudy.spsCodeKey === scheduleEntry.SPS_Code_Key);

			return {
				...scheduleEntry,
				rpCodeKey: currentSelectedStudy.rpCodeKey,
				selectedResourceAllocation: null
			};
		}), override, opCodeKey, orderKey);
	}

	public scheduleStudiesWithModel(model, override: RulesValidationOverride): HttpObservable<any> {
		return this.selfSchedulingProxy.scheduleStudiesWithModel(model, override);
	}

	public updateOrderWithModel(model, override: RulesValidationOverride, opCodeKey: number): HttpObservable<any> {
		return this.selfSchedulingProxy.updateOrderWithModel(model, override, opCodeKey);
	}

	public updateOrderWithoutModel(orderData: selfScheduling.IOrderUpdateData, override: RulesValidationOverride, opCodeKey: number, orderKey: number): HttpObservable<any> {
		return this.selfSchedulingProxy.updateOrderWithoutModel(orderData, override, opCodeKey, orderKey);
	}

	public SaveScanDocsWithOverride(orderData: selfScheduling.IOrderUpdateData, orderKey: number = -1): HttpObservable<any> {
		return this.selfSchedulingProxy.SaveScanDocsWithOverride(orderData, orderKey);
	}

	public announceScheduleChange(data: IPortalScheduleChangeMessage): HttpObservable<any> {
		return this.selfSchedulingProxy.announceScheduleChange(data)
	}

	public isSchedulingAllowed(model): HttpObservable<string> {
		return this.selfSchedulingProxy.isSchedulingAllowed(model);
	}

	private normalizeDaysTimeOptions(daysTimeOptions: selfScheduling.IDaysTimeOptions): selfScheduling.IDaysTimeOptions {
		return !daysTimeOptions ? null : {
			startTime: daysTimeOptions.startTime ? daysTimeOptions.startTime : moment().startOf("day").toDate(),
			endTime: daysTimeOptions.endTime ? daysTimeOptions.endTime : moment().endOf("day").toDate(),
			weekdays: daysTimeOptions.weekdays ? daysTimeOptions.weekdays : [true, true, true, true, true, true, true]
		};
	}

	public GetContactInfoHtml(languageKey: number): HttpObservable<string> {
		return this.selfSchedulingProxy.GetContactInfoHtml(languageKey);
	}

	public GetOrgInstructionsHtml(request: IOrgInstructionRequest): HttpObservable<string> {
		return this.selfSchedulingProxy.GetOrgInstructionsHtml(request);
	}

	public GetSPSInstructionsHtml(request: ISPSInstructionRequest): HttpObservable<string> {
		return this.selfSchedulingProxy.GetSPSInstructionsHtml(request);
	}

	public CheckRowsExistForOpCodeKey(opCodeKey: number): HttpObservable<boolean> {
		return this.selfSchedulingProxy.CheckRowsExistForOpCodeKey(opCodeKey);
	}

	public GetSchedulingSearchInterval(
		orderKey: number,
		opCodeKey: number,
		selectedStudies: selfScheduling.ISelectedStudy[]) {
		return this.selfSchedulingProxy.GetSchedulingSearchInterval(orderKey, opCodeKey, selectedStudies);
	}

	public logSessionError(opCodeKey: number, data: Object): HttpObservable<any> {
		return this.selfSchedulingProxy.logSessionError(opCodeKey, data);
	}

	public getAllowedDocumentTypesForPatient() {
		return this.selfSchedulingProxy.GetAllowedDocumentTypesForPatient();
	}
}
